"use strict"

/**
 * Product gallery zoom + full-size viewer (desktop only).
 *
 * - Hovering over a product image shows a magnifier lens. The lens uses the
 *   full-size source (data-full-src), so the zoom stays crisp even though
 *   the displayed image is a small CDN crop.
 * - Clicking an image opens a full-size viewer with wheel/button zoom,
 *   drag-to-pan and prev/next navigation through the visible gallery images.
 *
 * Works with the product builder's dynamically swapped images: reads
 * data-full-src (set by PTCProductBuilder.updateMediaSlot) and falls back to
 * stripping the Shopify resize suffix (_450x450_crop_center etc.) from the
 * current src.
 */
const PTCProductGalleryZoom = (() => {
    'use strict'

    const mqDesktop = window.matchMedia('(min-width: 64em)')
    const LENS_SIZE = 280
    const LENS_ZOOM = 2.2
    const ZOOM_STEP = 1.15
    const MAX_ZOOM_MULT = 8

    let container = null
    let lens = null
    let viewerEl = null
    let viewerImg = null
    let viewState = null
    let lensVisible = false
    let lensTimer = null

    /* ── Full-size source helpers ─────────────────────────────────────── */

    /**
     * Original (full-size) URL of a displayed image: prefer the dataset set
     * by the builder, otherwise strip the Shopify resize suffix from src.
     */
    function fullSrcOf(img) {
        if (img.dataset.fullSrc) return img.dataset.fullSrc;
        return (img.currentSrc || img.src).replace(/_\d+x\d+_crop_center(?=\.[a-z0-9]+)/i, '');
    }

    function isVisible(el) {
        return el && el.offsetParent !== null && el.getAttribute('aria-hidden') !== 'true';
    }

    /* ── Magnifier lens ───────────────────────────────────────────────── */

    function showLens(img, x, y) {
        if (!lens || !mqDesktop.matches || !isVisible(img)) return;
        const fullSrc = fullSrcOf(img);
        if (!fullSrc) return;

        const rect = img.getBoundingClientRect();
        const size = Math.min(LENS_SIZE, rect.width - 4, rect.height - 4);
        if (size <= 0) return;

        lens.style.width = size + 'px';
        lens.style.height = size + 'px';
        lens.style.backgroundImage = `url("${fullSrc.replace(/"/g, '\\"')}")`;
        // background covers exactly what the image shows, scaled up by LENS_ZOOM
        lens.style.backgroundSize = `${rect.width * LENS_ZOOM}px ${rect.height * LENS_ZOOM}px`;

        const containerRect = container.getBoundingClientRect();
        // keep the lens centered on the cursor but clamped inside the image
        let left = x - containerRect.left - size / 2;
        let top = y - containerRect.top - size / 2;
        const imgLeft = rect.left - containerRect.left;
        const imgTop = rect.top - containerRect.top;
        left = Math.max(imgLeft, Math.min(left, imgLeft + rect.width - size));
        top = Math.max(imgTop, Math.min(top, imgTop + rect.height - size));
        lens.style.left = left + 'px';
        lens.style.top = top + 'px';

        // position the zoomed region under the lens center
        const cx = Math.min(rect.width, Math.max(0, x - rect.left));
        const cy = Math.min(rect.height, Math.max(0, y - rect.top));
        lens.style.backgroundPosition =
            `${-cx * LENS_ZOOM + size / 2}px ${-cy * LENS_ZOOM + size / 2}px`;

        lens.classList.add('pgz-lens--visible');
        lensVisible = true;
    }

    function hideLens() {
        if (!lensVisible) return;
        lensVisible = false;
        if (lens) lens.classList.remove('pgz-lens--visible');
    }

    /* ── Full-size viewer ─────────────────────────────────────────────── */

    function viewerMarkup() {
        const el = document.createElement('div');
        el.className = 'pgz-viewer';
        el.hidden = true;
        el.innerHTML =
            '<button type="button" class="pgz-viewer__close" aria-label="Close">&#10005;</button>' +
            '<button type="button" class="pgz-viewer__arrow pgz-viewer__arrow--prev" aria-label="Previous">&#8249;</button>' +
            '<button type="button" class="pgz-viewer__arrow pgz-viewer__arrow--next" aria-label="Next">&#8250;</button>' +
            '<div class="pgz-viewer__stage">' +
            '  <img class="pgz-viewer__img" alt="">' +
            '</div>' +
            '<div class="pgz-viewer__controls">' +
            '  <button type="button" data-zoom="-1" aria-label="Zoom out">&#8722;</button>' +
            '  <span class="pgz-viewer__scale">100%</span>' +
            '  <button type="button" data-zoom="1" aria-label="Zoom in">+</button>' +
            '  <button type="button" data-zoom="0" class="pgz-viewer__fit">Fit</button>' +
            '</div>';
        return el;
    }

    function openViewer(imgs, index) {
        if (!mqDesktop.matches) return;
        if (!viewerEl) {
            viewerEl = viewerMarkup();
            document.body.appendChild(viewerEl);
            viewerEl.querySelector('.pgz-viewer__close').addEventListener('click', closeViewer);
            viewerEl.querySelector('.pgz-viewer__arrow--prev').addEventListener('click', () => stepViewer(-1));
            viewerEl.querySelector('.pgz-viewer__arrow--next').addEventListener('click', () => stepViewer(1));
            viewerEl.addEventListener('click', (e) => { if (e.target === viewerEl) closeViewer(); });
            viewerImg = viewerEl.querySelector('.pgz-viewer__img');
            viewerImg.addEventListener('load', () => {
                viewState.naturalW = viewerImg.naturalWidth;
                viewState.naturalH = viewerImg.naturalHeight;
                fitViewer(true);
            });
            viewerEl.querySelector('.pgz-viewer__controls').addEventListener('click', (e) => {
                const btn = e.target.closest('[data-zoom]');
                if (!btn) return;
                const v = Number(btn.dataset.zoom);
                if (v === 0) fitViewer(true);
                else zoomViewer(v > 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
            });
            // stage: wheel zoom + drag pan
            const stage = viewerEl.querySelector('.pgz-viewer__stage');
            stage.addEventListener('wheel', (e) => {
                e.preventDefault();
                zoomViewerAt(e.clientX, e.clientY, e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
            }, { passive: false });
            stage.addEventListener('pointerdown', (e) => {
                if (viewState.scale <= viewState.fit) return;
                viewState.dragging = { x: e.clientX, y: e.clientY, tx: viewState.tx, ty: viewState.ty };
                stage.setPointerCapture(e.pointerId);
                stage.classList.add('pgz-viewer__stage--dragging');
            });
            stage.addEventListener('pointermove', (e) => {
                if (!viewState.dragging) return;
                viewState.tx = viewState.dragging.tx + (e.clientX - viewState.dragging.x);
                viewState.ty = viewState.dragging.ty + (e.clientY - viewState.dragging.y);
                applyViewerTransform();
            });
            stage.addEventListener('pointerup', () => {
                viewState.dragging = null;
                stage.classList.remove('pgz-viewer__stage--dragging');
            });
            stage.addEventListener('pointercancel', () => {
                viewState.dragging = null;
                stage.classList.remove('pgz-viewer__stage--dragging');
            });
            document.addEventListener('keydown', onViewerKeydown);
        }

        const list = [];
        for (const img of imgs) {
            const fullSrc = fullSrcOf(img);
            if (fullSrc) list.push({ src: fullSrc, alt: img.alt || '' });
        }
        if (!list.length) return;
        viewState = {
            list,
            index: Math.max(0, Math.min(index, list.length - 1)),
            scale: 1,
            fit: 1,
            tx: 0,
            ty: 0,
            naturalW: 0,
            naturalH: 0,
            dragging: null,
        };

        document.body.style.overflow = 'hidden';
        viewerEl.hidden = false;
        viewerEl.querySelector('.pgz-viewer__arrow--prev').style.display = list.length > 1 ? '' : 'none';
        viewerEl.querySelector('.pgz-viewer__arrow--next').style.display = list.length > 1 ? '' : 'none';
        loadViewerImage();
    }

    function loadViewerImage() {
        const item = viewState.list[viewState.index];
        hideLens();
        viewerImg.style.transform = '';
        viewerImg.alt = item.alt;
        viewerImg.src = item.src; // load event -> fitViewer
        if (viewerImg.complete && viewerImg.naturalWidth) {
            viewState.naturalW = viewerImg.naturalWidth;
            viewState.naturalH = viewerImg.naturalHeight;
            fitViewer(true);
        }
    }

    function fitViewer(resetPan) {
        const stage = viewerEl.querySelector('.pgz-viewer__stage');
        const stageW = stage.clientWidth;
        const stageH = stage.clientHeight;
        if (!stageW || !viewState.naturalW) return;
        viewState.fit = Math.min((stageW - 48) / viewState.naturalW, (stageH - 48) / viewState.naturalH);
        if (viewState.fit > 1) viewState.fit = 1; // never upscale beyond natural
        viewState.scale = viewState.fit;
        viewState.tx = 0;
        viewState.ty = 0;
        applyViewerTransform();
    }

    function zoomViewer(factor) {
        const stage = viewerEl.querySelector('.pgz-viewer__stage');
        const rect = stage.getBoundingClientRect();
        zoomViewerAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
    }

    function zoomViewerAt(cx, cy, factor) {
        if (!viewState.naturalW) return;
        const maxScale = viewState.fit * MAX_ZOOM_MULT;
        const next = Math.min(maxScale, Math.max(viewState.fit, viewState.scale * factor));
        if (next === viewState.scale) return;
        // keep the point under the cursor fixed while zooming
        const px = (cx - viewState.tx) / viewState.scale;
        const py = (cy - viewState.ty) / viewState.scale;
        viewState.scale = next;
        viewState.tx = cx - px * next;
        viewState.ty = cy - py * next;
        clampViewerPan();
        applyViewerTransform();
    }

    function clampViewerPan() {
        const stage = viewerEl.querySelector('.pgz-viewer__stage');
        const w = viewState.naturalW * viewState.scale;
        const h = viewState.naturalH * viewState.scale;
        const maxX = Math.max(0, (w - stage.clientWidth) / 2);
        const maxY = Math.max(0, (h - stage.clientHeight) / 2);
        viewState.tx = Math.min(maxX, Math.max(-maxX, viewState.tx));
        viewState.ty = Math.min(maxY, Math.max(-maxY, viewState.ty));
    }

    function applyViewerTransform() {
        if (!viewState || !viewerImg) return;
        viewerImg.style.transform = `translate(${viewState.tx}px, ${viewState.ty}px) scale(${viewState.scale})`;
        const pct = Math.round((viewState.scale / (viewState.fit || 1)) * 100);
        const scaleEl = viewerEl.querySelector('.pgz-viewer__scale');
        if (scaleEl) scaleEl.textContent = pct + '%';
        viewerImg.classList.toggle('pgz-viewer__img--zoomed', viewState.scale > viewState.fit);
    }

    function stepViewer(dir) {
        if (!viewState || viewState.list.length < 2) return;
        viewState.index = (viewState.index + dir + viewState.list.length) % viewState.list.length;
        loadViewerImage();
    }

    function closeViewer() {
        if (!viewerEl || viewerEl.hidden) return;
        viewerEl.hidden = true;
        document.body.style.overflow = '';
        viewerImg.src = '';
        viewState = null;
    }

    function onViewerKeydown(e) {
        if (!viewerEl || viewerEl.hidden) return;
        if (e.key === 'Escape') closeViewer();
        else if (e.key === 'ArrowLeft') stepViewer(-1);
        else if (e.key === 'ArrowRight') stepViewer(1);
        else if (e.key === '+' || e.key === '=') zoomViewer(ZOOM_STEP);
        else if (e.key === '-') zoomViewer(1 / ZOOM_STEP);
    }

    /* ── Wiring ───────────────────────────────────────────────────────── */

    function onMouseMove(e) {
        if (!mqDesktop.matches) { hideLens(); return; }
        const img = e.target.closest && e.target.closest('img');
        if (!img || !img.closest('.images') || img.classList.contains('pgz-viewer__img')) { hideLens(); return; }
        showLens(img, e.clientX, e.clientY);
        clearTimeout(lensTimer);
    }

    function onClick(e) {
        if (!mqDesktop.matches) return;
        const img = e.target.closest && e.target.closest('img');
        if (!img || !img.closest('.images') || !isVisible(img)) return;
        const imgs = [...container.querySelectorAll('img')].filter(isVisible);
        openViewer(imgs, imgs.indexOf(img));
    }

    function init() {
        container = document.querySelector('.prodImgs .images');
        if (!container) return; // not a product builder page
        if (document.querySelector('.pgz-lens')) return; // already initialised

        lens = document.createElement('div');
        lens.className = 'pgz-lens';
        container.appendChild(lens);

        container.addEventListener('mousemove', onMouseMove);
        container.addEventListener('mouseleave', hideLens);
        container.addEventListener('click', onClick);
        window.addEventListener('scroll', hideLens, { passive: true });
    }

    return { init };
})();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => PTCProductGalleryZoom.init());
} else {
    PTCProductGalleryZoom.init();
}

if (typeof window !== 'undefined') {
    window.PTCProductGalleryZoom = PTCProductGalleryZoom;
}
